'use client';

import type { VisitorCurationGoodsItem } from '@musinsa/services-curator';
import type { HTMLAttributes, ReactNode } from 'react';
import { ProductInformationPrimitive } from '../../../common';
import { ProductShell } from './ProductShell';

interface VisitorCurationProductProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
  item: VisitorCurationGoodsItem;
  checked?: boolean;
  index?: number;
  children?: ReactNode;
  imageClassName?: string;
  informationClassName?: string;
  informmationChildren?: ReactNode;
  onClickCheckbox?: () => void;
  onClickCloseButton?: () => void;
  onClickLikeButton?: (item: VisitorCurationGoodsItem, nextIsLiked: boolean) => void;
  /** 29cm 딥링크 활성화 여부 */
  enableDeeplink?: boolean;
  callbackClick?: (item: VisitorCurationGoodsItem) => void;
  callbackImpression?: (item: VisitorCurationGoodsItem) => void;
}

export const VisitorCurationProduct = ({
  className,
  item,
  checked,
  index,
  children,
  imageClassName,
  informationClassName,
  informmationChildren,
  onClickCheckbox,
  onClickCloseButton,
  onClickLikeButton,
  enableDeeplink = false,
  callbackClick,
  callbackImpression,
  ...rest
}: VisitorCurationProductProps) => {
  const infoBlock = (
    <ProductInformationPrimitive.Container className={informationClassName}>
      <div>
        <ProductInformationPrimitive.BrandName brandName={item.brandName} />
        <ProductInformationPrimitive.GoodsName goodsName={item.goodsName} />
      </div>

      <div className="flex flex-col">
        <ProductInformationPrimitive.DiscountPriceRow
          finalPrice={item.finalPrice ?? item.price}
          finalDiscount={item.finalDiscount ?? item.salesRate ?? 0}
          hasOptionPrice={item.hasOptionPrice ?? undefined}
        />

        <ProductInformationPrimitive.EngagementRow>
          <ProductInformationPrimitive.HeartLikeCount likeCount={item.likeCount ?? 0} />
          {item.reviewScore && (
            <ProductInformationPrimitive.ReviewScore reviewScore={item.reviewScore} />
          )}
          <ProductInformationPrimitive.ReviewCount reviewCount={item.reviewCount ?? 0} />
        </ProductInformationPrimitive.EngagementRow>

        {(item.existsCoupon || item.isExclusive || item.isLowestPrice) && (
          <ProductInformationPrimitive.ProductBadgeContainer>
            {item.existsCoupon && <ProductInformationPrimitive.CouponBadge />}
            {item.isExclusive && <ProductInformationPrimitive.ExclusiveGoodsBadge />}
            {/* 단독 라벨이 있으면 최저가도전 미노출 (Figma 정책) */}
            {item.isLowestPrice && !item.isExclusive && (
              <ProductInformationPrimitive.LowestPriceBadge />
            )}
          </ProductInformationPrimitive.ProductBadgeContainer>
        )}

        {item.expectedEarnings != null && (
          <ProductInformationPrimitive.ExpectedEarnings expectedEarnings={item.expectedEarnings} />
        )}
      </div>

      {informmationChildren && (
        <ProductInformationPrimitive.Footer>
          {informmationChildren}
        </ProductInformationPrimitive.Footer>
      )}
    </ProductInformationPrimitive.Container>
  );

  return (
    <ProductShell
      className={className}
      item={item}
      checked={checked}
      index={index}
      imageClassName={imageClassName}
      infoBlock={infoBlock}
      onClickCheckbox={onClickCheckbox}
      onClickCloseButton={onClickCloseButton}
      onClickLikeButton={onClickLikeButton}
      enableDeeplink={enableDeeplink}
      productUrl={item.linkUrl}
      callbackClick={callbackClick}
      callbackImpression={callbackImpression}
      {...rest}
    >
      {children}
    </ProductShell>
  );
};
